/**
 * Monitoring & Analytics Setup
 * - Sentry for error tracking and performance monitoring
 * - PostHog for product analytics
 */

import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

// Environment detection
const isProd = import.meta.env.PROD;
const isDev = import.meta.env.DEV;

// Configuration from environment variables
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

/**
 * Initialize Sentry for error tracking and performance monitoring
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    if (isDev) {
      console.log('[Monitoring] Sentry DSN not configured, skipping initialization');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Capture 10% of sessions for replay
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Set tracesSampleRate to capture performance data
    // In production, adjust this value based on traffic
    tracesSampleRate: isProd ? 0.2 : 1.0,

    // Capture Replay for errors
    replaysSessionSampleRate: isProd ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,

    // Environment tagging
    environment: isProd ? 'production' : 'development',

    // Filter out noisy errors
    beforeSend(event, hint) {
      // Don't send errors in development unless explicitly enabled
      if (isDev && !import.meta.env.VITE_SENTRY_DEBUG) {
        console.log('[Sentry] Error captured (not sent in dev):', hint.originalException);
        return null;
      }

      // Filter out known non-critical errors
      const error = hint.originalException;
      if (error instanceof Error) {
        // Ignore network errors that are expected
        if (error.message.includes('Network Error') && error.message.includes('health')) {
          return null;
        }
        // Ignore cancelled requests
        if (error.message.includes('canceled') || error.message.includes('aborted')) {
          return null;
        }
      }

      return event;
    },

    // Add custom tags
    initialScope: {
      tags: {
        app: 'bess-optimization',
        version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      },
    },
  });

  console.log('[Monitoring] Sentry initialized');
}

/**
 * Initialize PostHog for product analytics
 */
export function initPostHog() {
  if (!POSTHOG_KEY) {
    if (isDev) {
      console.log('[Monitoring] PostHog key not configured, skipping initialization');
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,

    // Capture pageviews automatically
    capture_pageview: true,

    // Capture page leaves for session tracking
    capture_pageleave: true,

    // Enable session recording (if available in your plan)
    disable_session_recording: false,

    // Respect Do Not Track
    respect_dnt: true,

    // Disable in development unless explicitly enabled
    loaded: (posthog) => {
      if (isDev && !import.meta.env.VITE_POSTHOG_DEBUG) {
        posthog.opt_out_capturing();
        console.log('[PostHog] Opted out in development mode');
      } else {
        console.log('[Monitoring] PostHog initialized');
      }
    },

    // Bootstrap with feature flags if needed
    bootstrap: {
      featureFlags: {},
    },

    // Autocapture settings
    autocapture: {
      dom_event_allowlist: ['click', 'submit', 'change'],
      element_allowlist: ['button', 'a', 'input', 'select', 'form'],
    },
  });
}

/**
 * Initialize all monitoring services
 */
export function initMonitoring() {
  initSentry();
  initPostHog();
}

/**
 * Identify user for both Sentry and PostHog
 */
export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  // Sentry user identification
  Sentry.setUser({
    id: userId,
    ...traits,
  });

  // PostHog user identification
  if (POSTHOG_KEY) {
    posthog.identify(userId, traits);
  }
}

/**
 * Clear user identification (on logout)
 */
export function clearUserIdentity() {
  Sentry.setUser(null);

  if (POSTHOG_KEY) {
    posthog.reset();
  }
}

/**
 * Track a custom event in PostHog
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (POSTHOG_KEY) {
    posthog.capture(eventName, properties);
  }
}

/**
 * Track performance metrics
 */
export function trackPerformance(name: string, duration: number, metadata?: Record<string, unknown>) {
  // Track in PostHog
  trackEvent('performance_metric', {
    metric_name: name,
    duration_ms: duration,
    ...metadata,
  });

  // Add breadcrumb in Sentry
  Sentry.addBreadcrumb({
    category: 'performance',
    message: `${name}: ${duration}ms`,
    level: 'info',
    data: metadata,
  });
}

/**
 * Capture an error in Sentry with additional context
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Add a breadcrumb for debugging
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

// Re-export Sentry components for error boundaries
export { Sentry };
export { posthog };
